import React, { useMemo } from "react";
import { Link } from "react-router-dom";

/**
 * ProductPreview — a stylised, static (but animated) screenshot of the
 * Marianne workspace. Pure HTML/CSS + Tailwind, no external image. Mimics the
 * command-palette moment where a user searches "cheque" and sees results
 * blending Envelopes, Learn, and Case Starters.
 */
export default function ProductPreview() {
  // Deterministic per-render — results pre-populated so the user sees
  // something real-looking without an API call.
  const results = useMemo(() => [
    { kind: "envelope", title: "Cheque bounce notice — Acme Co.", meta: "sent · 2 signers", color: "#7B56C9" },
    { kind: "starter", title: "Cheque Bounce (S.138 NI Act)", meta: "Case Starter · ~8 min", color: "#C5A059" },
    { kind: "knowledge", title: "Clause Library: Cheque Dishonour — Workflow", meta: "Knowledge · Workflow", color: "#215a3b" },
    { kind: "case", title: "Chase vs Acme — pending summons", meta: "Case · Negotiable Instrument", color: "#2A0F5A" },
  ], []);

  return (
    <div className="relative w-full" data-testid="hero-product-preview">
      {/* Floating badge */}
      <div className="absolute -top-5 left-6 md:left-10 z-10 bg-white border border-[#E8E3F0] px-4 py-2 shadow-[0_8px_30px_rgb(42_15_90_/_0.08)]">
        <div className="label-eyebrow text-[#C5A059] text-[9px]">A moment in Marianne</div>
      </div>

      {/* Mock window */}
      <div className="bg-white border border-[#E8E3F0] shadow-[0_30px_60px_-15px_rgb(42_15_90_/_0.18)]">
        {/* Window chrome */}
        <div className="h-9 border-b border-[#E8E3F0] bg-[#FAFAF8] flex items-center px-4 gap-2">
          <div className="w-2.5 h-2.5 rounded-full bg-[#E8E3F0]" />
          <div className="w-2.5 h-2.5 rounded-full bg-[#E8E3F0]" />
          <div className="w-2.5 h-2.5 rounded-full bg-[#E8E3F0]" />
          <div className="flex-1 flex items-center justify-center">
            <span className="label-eyebrow text-[9px]">marianne.law / workspace</span>
          </div>
        </div>

        <div className="grid md:grid-cols-4">
          {/* Sidebar */}
          <aside className="hidden md:block border-r border-[#E8E3F0] py-5 bg-[#FAFAF8]">
            <div className="px-5 label-eyebrow mb-4">Workspace</div>
            <ul className="space-y-0.5 text-[12px]">
              {[
                { l: "Dashboard", active: false },
                { l: "Matters", active: false },
                { l: "Agreements", active: false },
                { l: "Signatures", active: true },
                { l: "Cases", active: false },
                { l: "Evidence", active: false },
                { l: "Calendar", active: false },
                { l: "Learn", active: false },
              ].map((it) => (
                <li
                  key={it.l}
                  className={`px-5 py-1.5 flex items-center gap-2 ${it.active ? "bg-white border-l-2 border-[#2A0F5A] font-medium text-[#2A0F5A]" : "text-[#4A5568]"}`}
                >
                  <span className="w-1 h-1 rounded-full bg-current opacity-30" />
                  {it.l}
                </li>
              ))}
            </ul>
          </aside>

          {/* Main — command palette moment */}
          <main className="md:col-span-3 p-5 md:p-8 min-h-[320px] relative">
            <div className="label-eyebrow mb-2">Search · ⌘K</div>
            <h3 className="font-serif text-2xl md:text-3xl tracking-tight mb-5">
              Everything you need, <span className="italic text-[#3D1F85]">one keystroke away.</span>
            </h3>

            {/* Mock search bar */}
            <div className="border border-[#2A0F5A] bg-white px-4 py-3 flex items-center gap-3">
              <span className="label-eyebrow text-[#C5A059]">⌘K</span>
              <span className="font-mono text-sm text-[#2A0F5A]">
                cheque
                <span className="inline-block w-[2px] h-[14px] bg-[#2A0F5A] ml-0.5 align-middle hp-cursor" />
              </span>
            </div>

            {/* Mock results — staggered reveal */}
            <ul className="mt-3 border border-[#E8E3F0] divide-y divide-[#E8E3F0] bg-white">
              {results.map((r, i) => (
                <li
                  key={r.title}
                  className="px-4 py-2.5 flex items-start gap-3 hp-row"
                  style={{ animationDelay: `${0.4 + i * 0.12}s` }}
                >
                  <span className="w-1 h-6 shrink-0 mt-0.5" style={{ background: r.color }} />
                  <div className="flex-1 min-w-0">
                    <div className="label-eyebrow text-[9px] mb-0.5">{r.kind}</div>
                    <div className="text-[13px] text-[#2A0F5A] font-medium truncate">{r.title}</div>
                    <div className="text-[11px] text-[#4A5568]">{r.meta}</div>
                  </div>
                </li>
              ))}
            </ul>

            {/* Tiny caption */}
            <p className="mt-5 text-[11px] text-[#4A5568] italic">
              Agreements · Envelopes · Cases · Case Starters · Knowledge Base — searched together.
            </p>
          </main>
        </div>
      </div>

      <style>{`
        @keyframes hp-rise { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        .hp-row { opacity: 0; animation: hp-rise 0.5s ease-out forwards; }
        @keyframes hp-blink { 0%, 50% { opacity: 1 } 51%, 100% { opacity: 0 } }
        .hp-cursor { animation: hp-blink 1s steps(1) infinite; }
      `}</style>
    </div>
  );
}
